import { Component, input, output, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from 'src/app/components/icon/icon.component';
import { AlertIndicatorVariation, RelationshipLevel } from '../models/wallet-alert-report.models';
import { IconName } from 'src/app/components/icon/icons/icon-map';
import { EllipsisLoadingComponent } from 'src/app/shared/components/ellipsis-loading/ellipsis-loading.component';
import { TruncatedTextComponent } from 'src/app/shared/components/truncated-text/truncated-text.component';
import {
  resolveIndicatorLabel,
  getRelationshipLevelSuffix,
  parsePartnerRegistrationChangedFields,
  parseShareholdingPartnerCount,
  INDICATOR_ICON_MAP,
} from '../models/wallet-alert-report.constants';

@Component({
  selector: 'app-alert-variation-card',
  imports: [CommonModule, IconComponent, EllipsisLoadingComponent, TruncatedTextComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './alert-variation-card.component.html',
  styleUrl: './alert-variation-card.component.scss',
})
export class AlertVariationCardComponent {
  data = input.required<AlertIndicatorVariation>();
  cardClick = output<void>();

  protected isLoadingPlaceholder = computed(() => !!this.data().isLoadingPlaceholder);
  protected isRegistrationData = computed(() => !!this.data().isRegistrationData);
  protected isPartnerRegistrationData = computed(() => !!this.data().isPartnerRegistrationData);
  protected isPartnerShareholdingData = computed(() => !!this.data().isPartnerShareholdingData);
  protected isPartnerCard = computed(() => {
    const level = this.data().relationshipLevel;
    return level === RelationshipLevel.L1 || level === RelationshipLevel.L2;
  });

  protected isClickableCard = computed(
    () => (this.isPartnerCard() || this.isRegistrationData()) && !this.data().isLoadingPlaceholder,
  );

  protected onCardClick(): void {
    if (this.isClickableCard()) {
      this.cardClick.emit();
    }
  }

  protected iconName = computed<IconName>(() => {
    const key = this.data().indicatorGroup?.toLowerCase() || '';
    const match = Object.keys(INDICATOR_ICON_MAP).find((k) => key.includes(k));
    return (
      (match ? INDICATOR_ICON_MAP[match as keyof typeof INDICATOR_ICON_MAP] : undefined) ??
      'help_outlined'
    );
  });

  protected isScore = computed(() => this.data().indicatorGroup?.toLowerCase().includes('score'));

  protected isPreviousScoreLetter = computed(() => {
    const prev = this.data().previousValue ?? this.data().previousQuantity;

    return this.isScore() && prev != null && isNaN(Number(prev));
  });

  protected hasQuantity = computed(
    () => !!this.data().previousQuantity && !!this.data().currentQuantity,
  );

  protected hasValue = computed(() => !!this.data().previousValue);

  protected viewModel = computed(() => {
    const d = this.data();

    if (d.isLoadingPlaceholder) {
      return { type: 'loading' as const };
    }

    if (d.isRegistrationData) {
      return {
        type: 'registration' as const,
        indicatorGroup:
          resolveIndicatorLabel(d.indicatorId, d.indicatorGroup) +
          getRelationshipLevelSuffix(d.relationshipLevel),
        fieldLabel: parsePartnerRegistrationChangedFields(d.currentValue ?? ''),
      };
    }

    if (d.isPartnerRegistrationData) {
      return {
        type: 'partner-registration' as const,
        indicatorGroup:
          resolveIndicatorLabel(d.indicatorId, d.indicatorGroup) +
          getRelationshipLevelSuffix(d.relationshipLevel),
        changedFields: parsePartnerRegistrationChangedFields(d.currentValue ?? ''),
      };
    }

    if (d.isPartnerShareholdingData) {
      const count = parseShareholdingPartnerCount(d.currentValue ?? '');
      const isL1 = d.relationshipLevel === RelationshipLevel.L1;
      const partnersLabel = isL1
        ? `${count} ` + $localize`:@@walletAlertVirtualGroupSocios: Socios`
        : `${count} ` +
          $localize`:@@walletAlertVirtualGroupOutrasParticipacoes: Otras Participaciones`;
      return {
        type: 'partner-shareholding' as const,
        indicatorGroup:
          resolveIndicatorLabel(d.indicatorId, d.indicatorGroup) +
          getRelationshipLevelSuffix(d.relationshipLevel),
        partnersLabel,
      };
    }

    const prevQ = Number(d.previousQuantity) || 0;
    const currQ = Number(d.currentQuantity) || 0;
    const prevV = Number(d.previousValue) || 0;
    const currV = Number(d.currentValue || '0');

    const diffQ = currQ - prevQ;
    const diffV = currV - prevV;

    const isScoreIndicator = this.isScore();
    const isBad = isScoreIndicator ? currQ < prevQ : currQ > prevQ;
    const type: 'success' | 'danger' | 'info' = isBad ? 'danger' : 'success';

    return {
      indicatorGroup:
        resolveIndicatorLabel(d.indicatorId, d.indicatorGroup) +
        getRelationshipLevelSuffix(d.relationshipLevel),
      diffQ,
      formattedDiffQ: diffQ > 0 ? `(+${diffQ})` : `(${diffQ})`,
      diffV,
      alertType: type,
      pointsLabel: isScoreIndicator ? 'pontos' : '',
      scoreLabel: diffQ >= 0 ? 'Variação positiva:' : 'Variação negativa:',
      prevQ,
      currQ,
      prevV,
      currV,
    };
  });
}
